import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { message, Modal, Button } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { NAMESPACE, COLOR } from '@/constants';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';
import { PRIVATE_PATH } from '@/constants/paths';
import {
  useGetCalculatorPackageListQuery,
  useDeleteCalculatorPackageMutation
} from '@/api/calculatorPackages';
import type { CalculatorPackage } from '@/api/calculatorPackages';
import { usePagination } from '@/hooks/usePagination';
import { useSearch } from '@/hooks/useSearch';
import { useRole } from '@/hooks/useRole';
import { APP_SCOPE } from '@/constants/roles';
import { ConfirmModal, Tag } from '@/components';
import { useTheme } from 'styled-components';

const { ChannelTag } = Tag;

const DEFAULT_SORT = 'createdAt';
const DEFAULT_ORDER = -1 as 1 | -1;

function formatDate(value?: string) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return value;
  }
}

type UseCustomPackagesOptions = {
  onDuplicatePackage: (pkg: CalculatorPackage) => void;
};

export function useCustomPackages({ onDuplicatePackage }: UseCustomPackagesOptions) {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const navigate = useNavigate();
  const { limit, page, handlePaginationChange, resetPage } = usePagination({ page: 1, limit: 10 });
  const [channelFilter, setChannelFilter] = useState<string | undefined>(undefined);
  const { search, handleSearch } = useSearch();
  const { canDelete } = useRole(APP_SCOPE.CUSTOM_PACKAGE);

  const baseListQuery = useMemo(
    () => ({
      sort: DEFAULT_SORT,
      order: DEFAULT_ORDER,
      ...(channelFilter ? { channel: channelFilter } : {}),
      ...(search ? { search } : {})
    }),
    [channelFilter, search]
  );

  const { data, isFetching, isLoading } = useGetCalculatorPackageListQuery({
    page,
    limit,
    ...baseListQuery
  });

  // Fetch ALL packages (no channel filter) so the Channel dropdown can offer
  // every channel that exists, not just channels present on the current page.
  const { data: dataForChannelOptions } = useGetCalculatorPackageListQuery({
    page: 1,
    limit: 5000,
    sort: DEFAULT_SORT,
    order: DEFAULT_ORDER
  });

  const [deletePackage, { isLoading: isDeleting }] = useDeleteCalculatorPackageMutation();
  const { color } = useTheme();
  const { ConfirmModalTitle } = ConfirmModal;

  const onDeleteClick = useCallback(
    (pkg: CalculatorPackage) => {
      Modal.confirm({
        title: (
          <ConfirmModalTitle style={{ color: COLOR.danger }}>Delete package</ConfirmModalTitle>
        ),
        icon: <ExclamationCircleOutlined style={{ color: color?.danger ?? '#ff4d4f' }} />,
        content: 'Are you sure you want to delete this package?',
        okText: t('cancel'),
        cancelButtonProps: {
          type: 'text',
          danger: true,
          style: { color: COLOR.danger }
        },
        cancelText: t('delete'),
        onCancel: () => {
          deletePackage(pkg._id)
            .unwrap()
            .then(() => message.success(t('packageDeleted')))
            .catch(() => {});
        }
      });
    },
    [deletePackage, t, color]
  );

  // Reset to page 1 whenever the channel filter or search term changes.
  useEffect(() => {
    resetPage();
  }, [channelFilter, search, resetPage]);

  const channelOptions = useMemo(
    () =>
      Array.from(new Set((dataForChannelOptions?.data ?? []).map((p) => p.channel).filter(Boolean)))
        .sort()
        .map((ch) => ({ value: ch, label: ch })),
    [dataForChannelOptions?.data]
  );

  const serverCount = data?.count ?? 0;
  const serverList = data?.data ?? [];
  const count = serverCount;
  const list = serverList;

  const onView = useCallback(
    (pkg: CalculatorPackage) => {
      navigate(`${PRIVATE_PATH.CUSTOM_PACKAGE}/${pkg._id}`);
    },
    [navigate]
  );

  const onEdit = useCallback(
    (pkg: CalculatorPackage) => {
      navigate(`${PRIVATE_PATH.CUSTOM_PACKAGE}/${pkg._id}?edit=1`);
    },
    [navigate]
  );

  const onDelete = onDeleteClick;

  const onDuplicate = useCallback(
    (pkg: CalculatorPackage) => {
      onDuplicatePackage(pkg);
    },
    [onDuplicatePackage]
  );

  const columns: ColumnsType<CalculatorPackage> = useMemo(
    () => [
      {
        title: t('packageName'),
        dataIndex: 'name',
        key: 'name',
        width: 220,
        ellipsis: true,
        render: (name: string, record) => (
          <a onClick={() => onView(record)} style={{ cursor: 'pointer', color: APP_SHELL_THEME.accent }}>
            {name || '—'}
          </a>
        )
      },
      {
        title: t('channel'),
        dataIndex: 'channel',
        key: 'channel',
        width: 140,
        render: (channel: string) => (channel ? <ChannelTag>{channel}</ChannelTag> : '—')
      },
      {
        title: t('createdDate'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 140,
        render: formatDate
      },
      {
        title: t('actions'),
        key: 'actions',
        width: 260,
        fixed: 'right',
        render: (_, record) => (
          <>
            <a onClick={() => onView(record)} style={{ marginRight: 12, color: APP_SHELL_THEME.textBody }}>
              {t('view')}
            </a>
            <a onClick={() => onEdit(record)} style={{ marginRight: 12, color: APP_SHELL_THEME.textBody }}>
              {t('edit')}
            </a>
            <a onClick={() => onDuplicate(record)} style={{ marginRight: 12, color: APP_SHELL_THEME.textBody }}>
              {t('duplicate')}
            </a>
            {canDelete && (
              <Button
                type="link"
                danger
                size="small"
                onClick={() => onDelete(record)}
                style={{ padding: 0, height: 'auto', color: COLOR.danger }}
              >
                {t('delete')}
              </Button>
            )}
          </>
        )
      }
    ],
    [t, onView, onEdit, onDuplicate, onDelete, canDelete]
  );

  return {
    list,
    columns,
    count,
    page,
    limit,
    isFetching,
    isLoading,
    handlePaginationChange,
    channelFilter,
    setChannelFilter,
    channelOptions,
    handleSearch,
    onView,
    onEdit,
    onDelete,
    isDeleting
  };
}
